import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  IconButton,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { groupPlayAPI, amenityAPI, membershipTierAPI, tenantSettingsAPI } from '../../services/api';
import {
  GroupPlay,
  GroupPlayInstance,
  GroupPlayStatus,
  UserRole,
  RecurringPattern,
  TierAccessConfiguration,
  TierAccessLevel,
  BookingWindowConfiguration,
  TenantSettings,
  MembershipTier,
} from '../../types';
import { formatDate, formatTimeString, formatDateForInput, parseDateOnlyString } from '../../utils/dateUtils';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AdminAccessControl from '../common/AdminAccessControl';
import BookGroupPlayDialog from './BookGroupPlayDialog';
import GroupPlayBookingsDialog from './GroupPlayBookingsDialog';

const GroupPlayManagementPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // State for group plays
  const [groupPlays, setGroupPlays] = useState<GroupPlay[]>([]);
  const [amenities, setAmenities] = useState<any[]>([]);
  const [membershipTiers, setMembershipTiers] = useState<MembershipTier[]>([]);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // State for group play instances (for recurring group plays)
  const [groupPlayInstances, setGroupPlayInstances] = useState<Record<string, GroupPlayInstance[]>>({});
  const [nextInstancesInfo, setNextInstancesInfo] = useState<Record<string, { confirmedCount: number; nextInstanceDate: string; capacity: number }>>({});

  // Dialog states
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openBookingsDialog, setOpenBookingsDialog] = useState(false);
  const [openBookDialog, setOpenBookDialog] = useState(false);
  const [selectedGroupPlay, setSelectedGroupPlay] = useState<GroupPlay | null>(null);
  const [selectedInstance, setSelectedInstance] = useState<GroupPlayInstance | null>(null);

  // Filter states
  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('SCHEDULED'); // Default to scheduled
  const [typeFilter, setTypeFilter] = useState<string>('');

  // Fetch data on component mount
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchGroupPlays(),
          fetchAmenities(),
          fetchMembershipTiers(),
          fetchTenantSettings(),
          fetchGroupPlayTypes(),
        ]);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  const fetchGroupPlays = async () => {
    try {
      const response = await groupPlayAPI.getGroupPlays();
      setGroupPlays(response.data);

      // Fetch instances for recurring group plays
      await fetchGroupPlayInstances(response.data);
    } catch (error: any) {
      console.error('Error fetching group plays:', error);
      setError('Failed to fetch group plays');
    }
  };

  const fetchGroupPlayInstances = async (groupPlayData: GroupPlay[]) => {
    try {
      const recurringGroupPlays = groupPlayData.filter(gp => gp.recurringPattern);

      // Get current date for filtering upcoming instances
      const today = new Date().toISOString().split('T')[0];

      // Fetch all recurring group play instances in parallel
      const results = await Promise.all(
        recurringGroupPlays.map(async (gp) => {
          try {
            const response = await groupPlayAPI.getGroupPlayInstances(gp.id, { startDate: today });
            return { id: gp.id, instances: response.data as GroupPlayInstance[] };
          } catch (error) {
            console.error(`Error fetching instances for group play ${gp.id}:`, error);
            return { id: gp.id, instances: [] as GroupPlayInstance[] };
          }
        })
      );

      const instancesData: Record<string, GroupPlayInstance[]> = {};
      const nextInstanceData: Record<string, { confirmedCount: number; nextInstanceDate: string; capacity: number }> = {};

      for (const { id, instances } of results) {
        instancesData[id] = instances;

        const upcomingInstances = instances.filter((inst) =>
          inst.sessionDate >= today && inst.status === GroupPlayStatus.SCHEDULED
        );

        if (upcomingInstances.length > 0) {
          const nextInstance = upcomingInstances[0];
          nextInstanceData[id] = {
            confirmedCount: nextInstance.confirmedCount || 0,
            nextInstanceDate: nextInstance.sessionDate,
            capacity: nextInstance.capacity,
          };
        }
      }

      setGroupPlayInstances(instancesData);
      setNextInstancesInfo(nextInstanceData);
    } catch (error) {
      console.error('Error fetching group play instances:', error);
    }
  };

  const fetchAmenities = async () => {
    try {
      const response = await amenityAPI.getAmenities();
      setAmenities(response.data);
    } catch (error: any) {
      console.error('Error fetching amenities:', error);
    }
  };

  const fetchMembershipTiers = async () => {
    try {
      const response = await membershipTierAPI.getMembershipTiers(true);
      setMembershipTiers(response.data.filter((tier: MembershipTier) => tier.active));
    } catch (error: any) {
      console.error('Error fetching membership tiers:', error);
    }
  };

  const fetchTenantSettings = async () => {
    try {
      const response = await tenantSettingsAPI.getAllSettings();
      setTenantSettings(response.data);
    } catch (error: any) {
      console.error('Error fetching tenant settings:', error);
    }
  };

  const fetchGroupPlayTypes = async () => {
    try {
      const response = await groupPlayAPI.getGroupPlayTypes();
      setAvailableTypes(response.data);
    } catch (error: any) {
      console.error('Error fetching group play types:', error);
    }
  };

  const handleCreateGroupPlay = () => {
    // Navigate to create page
    navigate('/group-plays/create');
  };

  const handleEditGroupPlay = (groupPlay: GroupPlay) => {
    // Navigate to edit page
    navigate(`/group-plays/edit/${groupPlay.id}`);
  };

  const handleDeleteGroupPlay = (groupPlay: GroupPlay) => {
    setSelectedGroupPlay(groupPlay);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteGroupPlay = async () => {
    if (!selectedGroupPlay) return;

    try {
      await groupPlayAPI.deleteGroupPlay(selectedGroupPlay.id);
      setGroupPlays(groupPlays.filter(gp => gp.id !== selectedGroupPlay.id));
      setOpenDeleteDialog(false);
      setSelectedGroupPlay(null);
      setSuccessMessage('Group play deleted successfully');
    } catch (error: any) {
      console.error('Error deleting group play:', error);
      setError(error.response?.data?.message || 'Failed to delete group play');
    }
  };

  const handleViewBookings = (groupPlay: GroupPlay, instance?: GroupPlayInstance) => {
    setSelectedGroupPlay(groupPlay);
    setSelectedInstance(instance || null);
    setOpenBookingsDialog(true);
  };

  const handleBookGroupPlay = (groupPlay: GroupPlay, instance?: GroupPlayInstance) => {
    setSelectedGroupPlay(groupPlay);
    setSelectedInstance(instance || null);
    setOpenBookDialog(true);
  };

  const handleCopyGroupPlay = (groupPlay: GroupPlay) => {
    // Navigate to create page with query params to copy from
    navigate(`/group-plays/create?copyFrom=${groupPlay.id}`);
  };

  const handleBookingSuccess = (message?: string) => {
    setOpenBookDialog(false);
    setSelectedGroupPlay(null);
    setSelectedInstance(null);
    if (message) {
      setSuccessMessage(message);
    }
    // Refresh group plays to update booking counts
    fetchGroupPlays();
  };

  const formatTime = (timeString: string) => {
    return formatTimeString(timeString);
  };

  const getStatusColor = (status: GroupPlayStatus) => {
    switch (status) {
      case GroupPlayStatus.SCHEDULED:
        return 'primary';
      case GroupPlayStatus.ACTIVE:
        return 'success';
      case GroupPlayStatus.COMPLETED:
        return 'default';
      case GroupPlayStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  // Filter group plays based on search and filter criteria
  const filteredGroupPlays = useMemo(() => {
    let filtered = [...groupPlays];

    // Text search (name, description)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(gp =>
        gp.name.toLowerCase().includes(searchLower) ||
        (gp.description && gp.description.toLowerCase().includes(searchLower))
      );
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(gp => gp.status === statusFilter);
    }

    // Type filter
    if (typeFilter) {
      filtered = filtered.filter(gp => gp.type === typeFilter);
    }

    return filtered;
  }, [groupPlays, searchText, statusFilter, typeFilter]);

  // Clear all filters
  const clearFilters = () => {
    setSearchText('');
    setStatusFilter('SCHEDULED'); // Reset to default
    setTypeFilter('');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg">
        <AdminAccessControl allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.EMPLOYEE, UserRole.COACH]}>
          <Box sx={{ mb: 4 }}>
            <Paper sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">Group Play Management</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateGroupPlay}
                >
                  Create Group Play
                </Button>
              </Box>

              {/* Search and Filter Section */}
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Search Group Plays"
                      placeholder="Search by name or description"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={statusFilter}
                        onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
                        label="Status"
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                        <MenuItem value="COMPLETED">Completed</MenuItem>
                        <MenuItem value="CANCELLED">Cancelled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={typeFilter}
                        onChange={(e: SelectChangeEvent) => setTypeFilter(e.target.value)}
                        label="Type"
                      >
                        <MenuItem value="">All Types</MenuItem>
                        {availableTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={clearFilters}
                      sx={{ height: '56px' }}
                    >
                      Clear Filters
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {/* Group Plays Table */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Date & Time</TableCell>
                      <TableCell>Capacity</TableCell>
                      <TableCell>Fees</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredGroupPlays.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No group plays found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredGroupPlays.map((groupPlay) => {
                        const nextInstanceInfo = nextInstancesInfo[groupPlay.id];
                        const isRecurring = !!groupPlay.recurringPattern;

                        return (
                          <TableRow key={groupPlay.id} hover>
                            <TableCell>
                              <Typography 
                                variant="body1" 
                                fontWeight="medium"
                                sx={{ 
                                  cursor: 'pointer', 
                                  color: 'primary.main',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                                onClick={() => navigate(`/group-plays/${groupPlay.id}`, { state: { from: 'group-play-management' } })}
                              >
                                {groupPlay.name}
                              </Typography>
                              {groupPlay.description && (
                                <Typography variant="body2" color="text.secondary">
                                  {groupPlay.description.length > 60
                                    ? `${groupPlay.description.substring(0, 60)}...`
                                    : groupPlay.description}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {groupPlay.type || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {isRecurring && nextInstanceInfo
                                  ? formatDate(nextInstanceInfo.nextInstanceDate)
                                  : formatDate(groupPlay.scheduleDate)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatTime(groupPlay.startTime)} - {formatTime(groupPlay.endTime)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ({groupPlay.durationMinutes} min)
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {isRecurring && nextInstanceInfo ? (
                                <>
                                  <Typography variant="body2">
                                    {nextInstanceInfo.confirmedCount} / {nextInstanceInfo.capacity}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    (Next session)
                                  </Typography>
                                </>
                              ) : (
                                <Typography variant="body2">
                                  {groupPlay.capacity} max
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2">
                                  Member: ${groupPlay.memberSessionFee}
                                </Typography>
                                {groupPlay.allowGuestBookings && groupPlay.guestSessionFee !== null && (
                                  <Typography variant="body2" color="text.secondary">
                                    Guest: ${groupPlay.guestSessionFee}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                  label={groupPlay.status}
                                  color={getStatusColor(groupPlay.status)}
                                  size="small"
                                />
                                {!groupPlay.active && (
                                  <Chip
                                    label="Inactive"
                                    color="default"
                                    size="small"
                                  />
                                )}
                                {isRecurring && (
                                  <Chip label="Recurring" color="info" size="small" />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => handleViewBookings(groupPlay)}
                                title="View Bookings"
                              >
                                <PeopleIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleBookGroupPlay(groupPlay)}
                                title="Book Member/Guest"
                              >
                                <PersonAddIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleCopyGroupPlay(groupPlay)}
                                title="Copy"
                              >
                                <CopyIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>

          {/* Delete Confirmation Dialog */}
          <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete "{selectedGroupPlay?.name}"?
                {selectedGroupPlay?.recurringPattern && (
                  <Box sx={{ mt: 2 }}>
                    <Alert severity="warning">
                      This will delete all instances of this recurring group play.
                    </Alert>
                  </Box>
                )}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
              <Button onClick={confirmDeleteGroupPlay} color="error" variant="contained">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Bookings Dialog */}
          {selectedGroupPlay && (
            <GroupPlayBookingsDialog
              open={openBookingsDialog}
              onClose={() => {
                setOpenBookingsDialog(false);
                setSelectedGroupPlay(null);
                setSelectedInstance(null);
              }}
              groupPlay={selectedGroupPlay}
              instance={selectedInstance}
              onRefresh={fetchGroupPlays}
            />
          )}

          {/* Book Dialog */}
          {selectedGroupPlay && (
            <BookGroupPlayDialog
              open={openBookDialog}
              onClose={() => {
                setOpenBookDialog(false);
                setSelectedGroupPlay(null);
                setSelectedInstance(null);
              }}
              groupPlay={selectedGroupPlay}
              instance={selectedInstance}
              onSuccess={handleBookingSuccess}
            />
          )}

          {/* Success Snackbar */}
          <Snackbar
            open={!!successMessage}
            autoHideDuration={6000}
            onClose={() => setSuccessMessage(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={() => setSuccessMessage(null)} severity="success">
              {successMessage}
            </Alert>
          </Snackbar>

          {/* Error Snackbar */}
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={() => setError(null)} severity="error">
              {error}
            </Alert>
          </Snackbar>
        </AdminAccessControl>
      </Container>
    </LocalizationProvider>
  );
};

export default GroupPlayManagementPage;
